import { useMemo } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Loader2, Sparkles, AlertTriangle, Plus } from "lucide-react";

/**
 * Banner that lists recipe ingredients NOT linked to inventory and
 * offers a one-click "Crear con IA" bulk action + per-item buttons.
 *
 * Only rendered when restaurantId is available and there is at least
 * one ingredient missing. Hidden in view mode (requires an explicit
 * "Editar receta" to avoid noise).
 */
export function MissingIngredientsBanner({
  ingredients = [],
  restaurantId,
  autoFillingIds,
  onCreate,
  onCreateAll,
}) {
  const missing = useMemo(() => {
    // Dedup by normalized name so the banner doesn't show two "Azúcar glas"
    // pills when a recipe lists the same ingredient twice (e.g. for filling
    // and for dusting). We keep the FIRST occurrence idx as the anchor.
    const seen = new Set();
    const out = [];
    ingredients.forEach((ing, idx) => {
      const name = (ing?.name || "").trim();
      if (!name || ingredients[idx]?.ingredient_id) return;
      const key = name.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ name, idx });
    });
    return out;
  }, [ingredients]);

  if (!restaurantId) return null;
  if (missing.length === 0) return null;

  const anyLoading = autoFillingIds && autoFillingIds.size > 0;

  return (
    <div
      className="mt-3 rounded-lg border border-amber-200 dark:border-amber-900/60 bg-amber-50/60 dark:bg-amber-950/20 p-3 space-y-2"
      data-testid="missing-ingredients-banner"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2 min-w-0">
          <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-xs font-semibold text-amber-900 dark:text-amber-200">
              {missing.length} ingrediente{missing.length > 1 ? "s" : ""} sin vincular al inventario
            </p>
            <p className="text-[11px] text-amber-700/80 dark:text-amber-300/80 mt-0.5 leading-relaxed">
              Podemos crearlo{missing.length > 1 ? "s" : ""} automáticamente con IA: nombre, unidad, coste, merma y categoría realistas para tu cocina.
            </p>
          </div>
        </div>
        {missing.length > 1 && (
          <Button
            size="sm"
            onClick={onCreateAll}
            disabled={anyLoading}
            className="gap-1.5 flex-shrink-0"
            data-testid="missing-ingredients-create-all-btn"
          >
            {anyLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {anyLoading ? `Creando (${autoFillingIds.size})…` : `Crear todos con IA`}
          </Button>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {missing.slice(0, 12).map(({ name, idx }) => {
          const thisLoading = autoFillingIds?.has(name);
          return (
            <Button
              key={`${name}-${idx}`}
              size="sm"
              variant="outline"
              disabled={thisLoading}
              onClick={() => onCreate(name, idx)}
              className="h-7 text-xs gap-1 bg-card/60 dark:bg-white/5 border-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30"
              data-testid={`missing-ingredient-btn-${idx}`}
              title={`Crear "${name}" en el inventario con IA`}
            >
              {thisLoading
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <Plus className="w-3 h-3" />}
              <span className="truncate max-w-[150px]">{name}</span>
            </Button>
          );
        })}
        {missing.length > 12 && (
          <Badge variant="outline" className="h-7 text-[10px] bg-card/60">
            +{missing.length - 12} más
          </Badge>
        )}
      </div>
    </div>
  );
}
